import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../contexts/ToastContext'
import Modal from '../../components/Modal'
import '../../components/Skeleton/Skeleton.css'
import CustomSelect from '../../components/CustomSelect/CustomSelect'
import './SchedulePage.css'

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]

const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate()
}

function getWeekday(year, month, day) {
  // 0=Sun, 6=Sat → convert to 0=Mon, 6=Sun
  const d = new Date(year, month - 1, day).getDay()
  return d === 0 ? 6 : d - 1
}

function calculateBreaksMinutes(breaks) {
  if (!breaks || breaks.length === 0) return 0
  let breaksMs = 0
  for (const b of breaks) {
    if (b.start && b.end) {
      breaksMs += new Date(b.end) - new Date(b.start)
    } else if (b.start && !b.end) {
      breaksMs += Date.now() - new Date(b.start).getTime()
    }
  }
  return Math.floor(breaksMs / 60000)
}

/* ---- Кастомный селектор сотрудников ---- */
function EmployeeSelector({ employees, value, onChange, getEmployeeColor }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  // Закрытие при клике вне
  useEffect(() => {
    if (!open) return
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Закрытие по Escape
  useEffect(() => {
    if (!open) return
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  const selectedEmp = employees.find(e => e.id === value)
  const selectedName = selectedEmp ? selectedEmp.name : 'Все сотрудники'
  const selectedColor = selectedEmp ? getEmployeeColor(selectedEmp.id) : null

  return (
    <div className="emp-selector" ref={ref}>
      <button
        className="emp-selector-trigger"
        onClick={() => setOpen(!open)}
        type="button"
      >
        <div className="emp-selector-left">
          {selectedColor ? (
            <span className="emp-selector-dot" style={{ background: selectedColor }} />
          ) : (
            <span className="emp-selector-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </span>
          )}
          <span className="emp-selector-name">{selectedName}</span>
        </div>
        <span className={`emp-selector-chevron ${open ? 'emp-selector-chevron-open' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </span>
      </button>

      {open && (
        <div className="emp-selector-dropdown">
          <button
            className={`emp-selector-option ${!value ? 'emp-selector-option-active' : ''}`}
            onClick={() => { onChange(''); setOpen(false) }}
            type="button"
          >
            <span className="emp-selector-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </span>
            <span>Все сотрудники</span>
            <span className="emp-selector-count">{employees.length}</span>
          </button>
          {employees.map(emp => (
            <button
              key={emp.id}
              className={`emp-selector-option ${value === emp.id ? 'emp-selector-option-active' : ''}`}
              onClick={() => { onChange(emp.id); setOpen(false) }}
              type="button"
            >
              <span className="emp-selector-dot" style={{ background: getEmployeeColor(emp.id) }} />
              <span>{emp.name}</span>
              {emp.role === 'helper' && <span className="emp-selector-badge">П</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function SchedulePage() {
  const { profile, isAdmin, activePvz, selectedPvzIds, userPvzs, adminPermissions: userPermissions } = useAuth()
  const toast = useToast()
  const canManageSchedule = isAdmin || userPermissions?.canEditSchedule === true
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [scheduleData, setScheduleData] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeDetailsPvzId, setActiveDetailsPvzId] = useState('')
  const [savingCell, setSavingCell] = useState(null) // "empId_day"
  const [editingCell, setEditingCell] = useState(null) // { empId, day }
  const [editValue, setEditValue] = useState('')
  const [editShiftRole, setEditShiftRole] = useState('employee')
  const [editStartTime, setEditStartTime] = useState('') // HH:MM
  const [editEndTime, setEditEndTime] = useState('') // HH:MM
  const [editMode, setEditMode] = useState('planned') // 'planned' | 'hours'
  const [filterEmployee, setFilterEmployee] = useState('')
  const [salaryConfig, setSalaryConfig] = useState(null)
  const [pendingRequests, setPendingRequests] = useState([])
  const [auditLogs, setAuditLogs] = useState([])
  const [showAuditLogs, setShowAuditLogs] = useState(false)
  const [pvzDetails, setPvzDetails] = useState(null)
  const [showAutoShiftSettings, setShowAutoShiftSettings] = useState(false)
  const [editPvzId, setEditPvzId] = useState('')
  
  const [aiForecastingEnabled, setAiForecastingEnabled] = useState(() => localStorage.getItem('aiForecastingEnabled') !== 'false')
  const [forecast, setForecast] = useState(null)
  const [loadingForecast, setLoadingForecast] = useState(false)
  const [forecastError, setForecastError] = useState(null)

  useEffect(() => {
    localStorage.setItem('aiForecastingEnabled', aiForecastingEnabled)
  }, [aiForecastingEnabled])

  const loadForecast = async (force = false) => {
    if (!activePvz?.id) return
    setLoadingForecast(true)
    setForecastError(null)
    try {
      const data = await api.getWorkloadForecast(activePvz.id, force)
      if (data?.unavailable) {
        setForecast(null)
        setForecastError(data.message || 'Сервис AI-прогноза временно недоступен')
      } else {
        setForecast(data)
      }
    } catch (err) {
      console.error('Failed to load forecast', err)
      setForecastError(err.message || 'Не удалось загрузить прогноз')
    } finally {
      setLoadingForecast(false)
    }
  }

  // Получить прогноз (или экстраполированный прогноз) нагрузки на определенный день месяца
  const getDayWorkload = useCallback((day) => {
    if (!forecast) return null;
    
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    
    // Сначала пробуем найти точный 7-дневный прогноз
    const dayForecast = forecast.forecast?.find(f => f.date === dateStr)
    if (dayForecast) {
      return dayForecast
    }
    
    // Если точного прогноза нет, экстраполируем с помощью исторических средних значений дня недели
    const wd = getWeekday(year, month, day)
    const WEEKDAYS_RU = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье']
    const dayOfWeekRu = WEEKDAYS_RU[wd]
    
    const hist = forecast.historicalAverages?.[dayOfWeekRu]
    const overall = forecast.overallAverages
    
    if (hist && overall) {
      const histVolume = (hist.avgAcceptance || 0) + (hist.avgIssued || 0)
      const overallVolume = (overall.avgAcceptance || 0) + (overall.avgIssued || 0)
      
      let workload = 'Medium'
      if (overallVolume > 0) {
        if (histVolume > overallVolume * 1.15) {
          workload = 'High'
        } else if (histVolume < overallVolume * 0.85) {
          workload = 'Low'
        }
      }
      
      let recommendation = 'Исторически нагрузка в этот день недели в пределах нормы.'
      if (workload === 'High') {
        recommendation = `Ожидается пиковая нагрузка (в среднем ${Math.round(histVolume)} оп.). Рекомендуется усилить смену.`
      } else if (workload === 'Low') {
        recommendation = `Ожидается пониженная нагрузка (в среднем ${Math.round(histVolume)} оп.).`
      }
      
      return {
        date: dateStr,
        dayOfWeek: dayOfWeekRu,
        workload,
        expectedAcceptance: Math.round(hist.avgAcceptance || 0),
        expectedIssued: Math.round(hist.avgIssued || 0),
        recommendation,
        isHistorical: true
      }
    }
    
    return null
  }, [forecast, year, month])


  const getPvzFullName = useCallback((pvzId) => {
    const pvz = userPvzs?.find(p => p.id === pvzId)
    return pvz ? pvz.name : ''
  }, [userPvzs])

  const getPvzShortName = useCallback((pvzId) => {
    const pvz = userPvzs?.find(p => p.id === pvzId)
    if (!pvz) return ''
    const cleanName = pvz.name.replace(/пвз|wildberries|wb|пункт|выдачи/gi, '').trim()
    const numberMatch = pvz.name.match(/\d+/)
    const parts = cleanName.split(/[\s,\-]+/).filter(Boolean)
    if (parts.length === 0) return pvz.name.slice(0, 4)
    if (numberMatch) {
      const num = numberMatch[0]
      const firstChar = parts[0] ? parts[0][0].toUpperCase() : ''
      return `${firstChar}${num}`
    }
    if (parts.length >= 2) {
      return parts.map(p => p[0].toUpperCase()).join('').slice(0, 3)
    }
    return parts[0].slice(0, 4)
  }, [userPvzs])

  const PVZ_COLORS = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#06b6d4', '#f43f5e']
  const getPvzColor = useCallback((pvzId) => {
    const idx = userPvzs?.findIndex(p => p.id === pvzId) ?? 0
    return PVZ_COLORS[Math.max(0, idx) % PVZ_COLORS.length]
  }, [userPvzs])

  const renderPvzBadge = (entry) => {
    if (!entry || !entry.pvz_id) return null
    if (!selectedPvzIds || selectedPvzIds.length <= 1) return null
    const shortName = getPvzShortName(entry.pvz_id)
    const pvzColor = getPvzColor(entry.pvz_id)
    return (
      <span 
        style={{
          fontSize: '8px',
          fontWeight: 800,
          color: pvzColor,
          background: `color-mix(in srgb, ${pvzColor} 8%, var(--bg-secondary))`,
          border: `1px solid color-mix(in srgb, ${pvzColor} 30%, var(--surface-border))`,
          borderRadius: '4px',
          padding: '1px 3px',
          marginTop: '2px',
          lineHeight: 1,
          display: 'inline-block'
        }}
        title={`Пункт: ${getPvzFullName(entry.pvz_id)}`}
      >
        {shortName}
      </span>
    )
  }

  const EMPLOYEE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f43f5e', '#6366f1', '#eab308']
  const getEmployeeColor = (empId) => {
    const idx = employees.findIndex(e => e.id === empId)
    return EMPLOYEE_COLORS[Math.max(0, idx) % EMPLOYEE_COLORS.length]
  }

  const filteredEmployees = (filterEmployee ? employees.filter(e => e.id === filterEmployee) : employees).filter(
    e => !e.is_legacy || scheduleData.some(s => s.employee_id === e.id)
  )

  const daysCount = getDaysInMonth(year, month)

  // Синхронизация активного ID ПВЗ для карточки автосмен
  useEffect(() => {
    if (selectedPvzIds && selectedPvzIds.length > 0) {
      if (!selectedPvzIds.includes(activeDetailsPvzId)) {
        setActiveDetailsPvzId(selectedPvzIds[0])
      }
    } else if (activePvz?.id) {
      setActiveDetailsPvzId(activePvz.id)
    }
  }, [selectedPvzIds, activePvz?.id, activeDetailsPvzId])

  // Загрузка настроек ЗП (включая флаг помощников)
  useEffect(() => {
    const pvzFilter = selectedPvzIds?.length > 0 ? selectedPvzIds.join(',') : (activePvz?.id || '')
    api.getSalaryConfig(pvzFilter)
      .then(setSalaryConfig)
      .catch(console.error)
  }, [activePvz?.id, selectedPvzIds])

  // Загрузка деталей ПВЗ (для автосмен)
  useEffect(() => {
    if (activeDetailsPvzId) {
      api.getPvzDetails(activeDetailsPvzId)
        .then(setPvzDetails)
        .catch(console.error)
    } else {
      setPvzDetails(null)
    }
  }, [activeDetailsPvzId])

  // Загрузка сотрудников
  useEffect(() => {
    if (!profile) return
    const pvzFilter = selectedPvzIds?.length > 0 ? selectedPvzIds.join(',') : (activePvz?.id || '')
    if (pvzFilter) {
      api.getEmployeesList(pvzFilter, profile.org_id).then(setEmployees).catch(console.error)
    } else if (!canManageSchedule) {
      setEmployees([{ id: profile.id, name: profile.full_name, role: profile.role }])
    }
  }, [canManageSchedule, profile?.id, profile?.org_id, activePvz?.id, selectedPvzIds])

  // Загрузка графика + ретроактивная синхронизация с work_logs
  const loadSchedule = useCallback(async () => {
    if (!profile?.id) return
    const pvzFilter = selectedPvzIds?.length > 0 ? selectedPvzIds.join(',') : (activePvz?.id || '')
    if (!pvzFilter) return
    setLoading(true)
    try {
      const data = await api.getSchedule(year, month)
      setScheduleData(data)
      
      if ((isAdmin || userPermissions?.canEditSchedule)) {
        const pending = await api.getPendingScheduleRequests(pvzFilter)
        setPendingRequests(pending)
        const logs = await api.getScheduleAuditLogs(pvzFilter)
        setAuditLogs(logs)
      }
    } catch (err) {
      console.error('Ошибка загрузки графика:', err)
    } finally {
      setLoading(false)
    }
  }, [year, month, profile?.id, activePvz?.id, selectedPvzIds, isAdmin, userPermissions])

  useEffect(() => {
    loadSchedule()
    
    // Автоматически загружаем ИИ-прогноз для подсветки дней в сетке, если включен ИИ
    if (canManageSchedule && activePvz?.id && aiForecastingEnabled) {
      loadForecast()
    }
    
    if (!selectedPvzIds || selectedPvzIds.length === 0) return
    
    const channels = selectedPvzIds.map(id => {
      return supabase.channel(`schedule_changes_${id}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'schedule',
          filter: `pvz_id=eq.${id}`
        }, () => {
          if (window.scheduleUpdateTimeout) clearTimeout(window.scheduleUpdateTimeout)
          window.scheduleUpdateTimeout = setTimeout(() => {
            loadSchedule()
          }, 500)
        })
        .subscribe()
    })
      
    return () => {
      channels.forEach(ch => supabase.removeChannel(ch))
    }
  }, [loadSchedule, selectedPvzIds, canManageSchedule, activePvz?.id, aiForecastingEnabled])

  const handleResolveRequest = async (empId, date, isApproved) => {
    // Оптимистичное обновление UI
    setPendingRequests(prev => prev.filter(req => !(req.employee_id === empId && req.work_date === date)))
    if (isApproved) {
      setScheduleData(prev => prev.map(s => (s.employee_id === empId && s.work_date === date) ? { ...s, approval_status: 'approved' } : s))
    } else {
      setScheduleData(prev => prev.filter(s => !(s.employee_id === empId && s.work_date === date)))
    }

    try {
      await api.resolveScheduleRequest(empId, date, isApproved)
      toast.success(isApproved ? 'Запрос одобрен' : 'Запрос отклонён')
      // Обновляем бейджи
      window.dispatchEvent(new Event('disputesUpdated'))
    } catch (err) {
      console.error('Ошибка обработки запроса', err)
      toast.error('Ошибка: ' + err.message)
      await loadSchedule() // Откатываем UI в случае ошибки
    }
  }

  const prevMonth = () => {
    if (month === 1) {
      setMonth(12)
      setYear(year - 1)
    } else {
      setMonth(month - 1)
    }
  }

  const nextMonth = () => {
    if (month === 12) {
      setMonth(1)
      setYear(year + 1)
    } else {
      setMonth(month + 1)
    }
  }

  const [editLinkedTo, setEditLinkedTo] = useState('')

  // Получить часы для конкретной ячейки
  const getHours = (empId, day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const entry = scheduleData.find(s => s.employee_id === empId && s.work_date === dateStr)
    return entry ? parseFloat(entry.hours) : 0
  }

  const getLinkedTo = (empId, day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const entry = scheduleData.find(s => s.employee_id === empId && s.work_date === dateStr)
    return entry ? entry.linked_to : ''
  }

  const getShiftRole = (empId, day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const entry = scheduleData.find(s => s.employee_id === empId && s.work_date === dateStr)
    return entry?.shift_role || 'employee'
  }

  // Phase 2: Получить полную запись для индикации
  const getEntry = (empId, day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return scheduleData.find(s => s.employee_id === empId && s.work_date === dateStr) || null
  }

  // Определяем тип ячейки: 'active' | 'actual' | 'planned' | 'legacy' | null
  const getCellType = (entry) => {
    if (!entry) return null
    if (entry.started_at && !entry.ended_at) return 'active' // Смена идёт
    if (entry.started_at && entry.ended_at) return 'actual'  // Завершена фактически
    if (entry.is_planned) return 'planned'                    // Плановая (галочка)
    if (parseFloat(entry.hours) > 0) return 'legacy'          // Legacy (до Phase 2) или ручной ввод
    return null
  }

  // Начать редактирование ячейки — всегда открывает модалку
  const startEdit = (empId, day) => {
    if (!canManageSchedule && empId !== profile.id) return

    const hours = getHours(empId, day)
    const entry = getEntry(empId, day)
    const emp = employees.find(e => e.id === empId) || {}
    setEditingCell({ empId, day })
    setEditValue(hours > 0 ? hours.toString() : '')
    setEditLinkedTo(getLinkedTo(empId, day) || '')
    
    const savedRole = getShiftRole(empId, day)
    setEditShiftRole(savedRole || emp.role || 'employee')
    
    // ПВЗ для редактирования: если есть entry, берем ее ПВЗ, иначе берем ПВЗ сотрудника или первый из выбранных
    setEditPvzId(entry?.pvz_id || emp.pvz_id || (selectedPvzIds && selectedPvzIds[0]) || activePvz?.id || '')
    
    // Определяем режим: если есть часы/время → режим часов, иначе → план
    const hasActualData = entry?.started_at || (hours > 0 && !entry?.is_planned)
    setEditMode(hasActualData ? 'hours' : 'planned')
    
    // Извлекаем локальное время из ISO
    const extractLocalTime = (isoStr) => {
      if (!isoStr) return ''
      const d = new Date(isoStr)
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
    setEditStartTime(entry?.started_at ? extractLocalTime(entry.started_at) : '')
    setEditEndTime(entry?.ended_at ? extractLocalTime(entry.ended_at) : '')
  }

  // Удалить запись из графика
  const deleteCell = async (empId, day) => {
    const cellKey = `${empId}_${day}`
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    
    setSavingCell(cellKey)
    setEditingCell(null)
    
    try {
      await api.upsertScheduleDay(empId, dateStr, 0, null, 'employee', null, null, isAdmin)
      setScheduleData(prev => prev.filter(s => !(s.employee_id === empId && s.work_date === dateStr)))
      if (aiForecastingEnabled) {
        loadForecast(true)
      }
    } catch (err) {
      console.error('Ошибка удаления:', err)
    } finally {
      setSavingCell(null)
    }
  }

  // Сохранить ячейку
  const saveCell = async (empId, day) => {
    const cellKey = `${empId}_${day}`
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const hours = parseFloat(editValue) || 0
    const emp = employees.find(e => e.id === empId) || {}
    
    // Если по профилю помощник — то всегда помощник. 
    // Если по профилю сотрудник — берем выбранную в модальном окне роль.
    const finalRole = emp.role === 'helper' ? 'helper' : (editShiftRole || 'employee')

    setSavingCell(cellKey)
    setEditingCell(null)

    try {
      // Вычисляем offset локальной таймзоны браузера (напр. +03:00)
      const tzOffsetMin = new Date().getTimezoneOffset()
      const tzSign = tzOffsetMin <= 0 ? '+' : '-'
      const tzAbs = Math.abs(tzOffsetMin)
      const tzOffset = `${tzSign}${String(Math.floor(tzAbs / 60)).padStart(2, '0')}:${String(tzAbs % 60).padStart(2, '0')}`

      if (editMode === 'planned') {
        // Режим «План» — ставим галочку
        await api.upsertSchedulePlanned(empId, dateStr, finalRole, editPvzId)
        setScheduleData(prev => {
          const filtered = prev.filter(s => !(s.employee_id === empId && s.work_date === dateStr))
          filtered.push({
            employee_id: empId, work_date: dateStr, hours: 0,
            is_planned: true, shift_role: finalRole, pvz_id: editPvzId
          })
          return filtered
        })
      } else if (editStartTime && editEndTime) {
        // Режим «Часы» — полная смена (начало + конец)
        const started_at = `${dateStr}T${editStartTime}:00${tzOffset}`
        const ended_at = `${dateStr}T${editEndTime}:00${tzOffset}`
        const diffMs = new Date(ended_at) - new Date(started_at)
        const calcHours = diffMs > 0 ? Math.round((diffMs / (1000 * 60 * 60)) * 2) / 2 : hours
        
        const td = new Date()
        const isPast = new Date(dateStr) < new Date(`${td.getFullYear()}-${String(td.getMonth() + 1).padStart(2, '0')}-${String(td.getDate()).padStart(2, '0')}`)
        const approvalStatus = (!isAdmin && isPast) ? 'pending' : 'approved'

        await api.upsertScheduleDay(empId, dateStr, Math.max(0.5, calcHours), editLinkedTo || null, finalRole, started_at, ended_at, isAdmin, editPvzId)
        
        if (approvalStatus === 'pending' && toast?.success) {
          toast.success('Запрос отправлен на рассмотрение владельцу')
        }
        
        setScheduleData(prev => {
          const filtered = prev.filter(s => !(s.employee_id === empId && s.work_date === dateStr))
          filtered.push({
            employee_id: empId, work_date: dateStr, hours: Math.max(0.5, calcHours),
            linked_to: editLinkedTo || null, shift_role: finalRole,
            started_at, ended_at, is_planned: false,
            approval_status: approvalStatus, pvz_id: editPvzId
          })
          return filtered
        })
      } else if (editStartTime && !editEndTime) {
        // Только начало — создаём активную смену (смена идёт)
        const started_at = `${dateStr}T${editStartTime}:00${tzOffset}`
        await api.upsertScheduleDay(empId, dateStr, 0, editLinkedTo || null, finalRole, started_at, null, isAdmin, editPvzId)
        
        setScheduleData(prev => {
          const filtered = prev.filter(s => !(s.employee_id === empId && s.work_date === dateStr))
          filtered.push({
            employee_id: empId, work_date: dateStr, hours: 0,
            linked_to: editLinkedTo || null, shift_role: finalRole,
            started_at, ended_at: null, is_planned: false, pvz_id: editPvzId
          })
          return filtered
        })
      } else if (hours > 0) {
        // Режим «Часы» только число без времени
        const td = new Date()
        const isPast = new Date(dateStr) < new Date(`${td.getFullYear()}-${String(td.getMonth() + 1).padStart(2, '0')}-${String(td.getDate()).padStart(2, '0')}`)
        const approvalStatus = (!isAdmin && isPast) ? 'pending' : 'approved'

        await api.upsertScheduleDay(empId, dateStr, hours, editLinkedTo || null, finalRole, null, null, isAdmin, editPvzId)
        
        if (approvalStatus === 'pending' && toast?.success) {
          toast.success('Запрос отправлен на рассмотрение владельцу')
        }

        setScheduleData(prev => {
          const filtered = prev.filter(s => !(s.employee_id === empId && s.work_date === dateStr))
          filtered.push({
            employee_id: empId, work_date: dateStr, hours,
            linked_to: editLinkedTo || null, shift_role: finalRole, is_planned: false,
            approval_status: approvalStatus, pvz_id: editPvzId
          })
          return filtered
        })
      }
      if (aiForecastingEnabled) {
        loadForecast(true)
      }
    } catch (err) {
      console.error('Ошибка сохранения:', err)
    } finally {
      setSavingCell(null)
    }
  }

  const handleKeyDown = (e, empId, day) => {
    if (e.key === 'Enter') {
      saveCell(empId, day)
    } else if (e.key === 'Escape') {
      setEditingCell(null)
    }
  }

  // Подсчёт итогов по сотруднику
  const getTotals = (empId) => {
    let days = 0
    let hours = 0
    for (let d = 1; d <= daysCount; d++) {
      const h = getHours(empId, d)
      const entry = getEntry(empId, d)
      if (h > 0 && !entry?.is_planned) {
        days++
        hours += h
      }
    }
    return { days, hours }
  }

  // Группировка сотрудников по ПВЗ при мульти-выборе
  const getGroupedEmployees = () => {
    if (!selectedPvzIds || selectedPvzIds.length <= 1) {
      return [{ pvzId: null, pvzName: null, emps: filteredEmployees }]
    }

    const groups = []
    
    selectedPvzIds.forEach(pvzId => {
      const pvzName = getPvzFullName(pvzId) || 'Пункт выдачи'
      const emps = filteredEmployees.filter(e => e.pvz_id === pvzId)
      if (emps.length > 0) {
        groups.push({ pvzId, pvzName, emps })
      }
    })

    const otherEmps = filteredEmployees.filter(e => !selectedPvzIds.includes(e.pvz_id))
    if (otherEmps.length > 0) {
      groups.push({ pvzId: 'other', pvzName: 'Другие сотрудники', emps: otherEmps })
    }

    if (groups.length === 0) {
      return [{ pvzId: null, pvzName: null, emps: filteredEmployees }]
    }

    return groups
  }

  return (
    <div className="schedule-page animate-fade-in">


      {/* Header */}
      <div className="schedule-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '20px' }}>
        <div className="schedule-header-left">
          <span className="schedule-header-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></span>
          <div>
            <h1 className="schedule-title">График работы</h1>
            <p className="schedule-subtitle">
              {canManageSchedule ? 'Рабочие дни и часы всех сотрудников' : 'Мой рабочий график'}
            </p>
          </div>
        </div>
        
        {canManageSchedule && (
          <div className="ai-forecasting-toggle-container">
            <span className="ai-toggle-stars-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></span>
            <span className="ai-toggle-text">ИИ-помощник смен</span>
            <label className="ai-toggle-switch">
              <input 
                type="checkbox" 
                checked={aiForecastingEnabled}
                onChange={(e) => {
                  const nextVal = e.target.checked
                  setAiForecastingEnabled(nextVal)
                  if (nextVal && !forecast) {
                    loadForecast()
                  }
                }}
              />
              <span className="ai-toggle-slider"></span>
            </label>
          </div>
        )}
      </div>

      {/* Month Navigation */}
      <div className="schedule-month-nav" data-tour="schedule-nav">
        <button className="btn btn-ghost btn-sm" onClick={prevMonth}>
          ← Пред.
        </button>
        <div className="schedule-month-title">
          {MONTH_NAMES[month - 1]} {year}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={nextMonth}>
          След. →
        </button>
      </div>


      {/* Автоматические смены */}
      {canManageSchedule && pvzDetails && (
        <div className="auto-shift-card" data-tour="schedule-autoshift">
          {/* Кликабельный заголовок для сворачивания/разворачивания */}
          <div 
            className="auto-shift-header"
            onClick={() => setShowAutoShiftSettings(!showAutoShiftSettings)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="auto-shift-icon-wrapper">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <div className="auto-shift-title-container">
                <strong className="auto-shift-title">Настройки автоматических смен и работы ПВЗ</strong>
                {showAutoShiftSettings && (
                  <span className="auto-shift-subtitle-detail">Пункт выдачи: {pvzDetails.name} • Часовой пояс: {pvzDetails.timezone || 'Europe/Moscow'}</span>
                )}
              </div>
            </div>

            {/* Вкладки выбора ПВЗ при мульти-выборе */}
            {showAutoShiftSettings && selectedPvzIds && selectedPvzIds.length > 1 && (
              <div className="auto-shift-pvz-tabs" onClick={(e) => e.stopPropagation()}>
                {(userPvzs || []).filter(p => selectedPvzIds.includes(p.id)).map(pvz => (
                  <button
                    key={pvz.id}
                    className={`auto-shift-pvz-tab ${activeDetailsPvzId === pvz.id ? 'active' : ''}`}
                    onClick={() => setActiveDetailsPvzId(pvz.id)}
                    type="button"
                  >
                    {pvz.name}
                  </button>
                ))}
              </div>
            )}

            {/* Статусные бейджи в свернутом состоянии */}
            {!showAutoShiftSettings && (
              <div className="auto-shift-badges">
                {pvzDetails.auto_open_shift && (
                  <span className="auto-shift-badge-active">
                    Автооткрытие в {pvzDetails.auto_open_time ? pvzDetails.auto_open_time.slice(0, 5) : '09:00'}
                  </span>
                )}
                {pvzDetails.auto_close_shift && (
                  <span className="auto-shift-badge-active">
                    Автозакрытие в {pvzDetails.auto_close_time ? pvzDetails.auto_close_time.slice(0, 5) : '21:00'}
                  </span>
                )}
                {!pvzDetails.auto_open_shift && !pvzDetails.auto_close_shift && (
                  <span className="auto-shift-badge-disabled">
                    Автосмены выключены
                  </span>
                )}
              </div>
            )}

            {/* Стрелочка шеврон */}
            <span className={`auto-shift-chevron ${showAutoShiftSettings ? 'open' : ''}`}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></span>
          </div>
          
          {/* Тело настроек (плавный аккордеон с grid-template-rows) */}
          <div className={`auto-shift-accordion ${showAutoShiftSettings ? 'open' : ''}`}>
            <div className="auto-shift-accordion-content">
              <div className="auto-shift-grid">
                
                {/* КОЛОНКА 1: Время работы пункта */}
                <div className="auto-shift-col">
                  <strong className="auto-shift-col-title">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                    Время работы пункта
                  </strong>
                  <span className="auto-shift-col-desc">Штатные часы открытия и закрытия ПВЗ</span>
                  
                  <div className="auto-shift-inputs-row">
                    <div className="auto-shift-input-group">
                      <label className="auto-shift-input-label">Открытие</label>
                      <input 
                        className="input" 
                        type="time" 
                        defaultValue={pvzDetails.open_time ? pvzDetails.open_time.slice(0, 5) : '09:00'}
                        onBlur={async (e) => {
                          const val = e.target.value;
                          if (!val) return;
                          try {
                            await api.updatePvz(pvzDetails.id, { open_time: val });
                            toast.success(`Время открытия ПВЗ обновлено: ${val}`);
                            setPvzDetails(prev => ({ ...prev, open_time: val }));
                          } catch (err) {
                            toast.error(err.message);
                          }
                        }}
                        style={{ height: '38px', fontSize: '14px', padding: '0 8px' }}
                      />
                    </div>
                    <div className="auto-shift-input-group">
                      <label className="auto-shift-input-label">Закрытие</label>
                      <input 
                        className="input" 
                        type="time" 
                        defaultValue={pvzDetails.close_time ? pvzDetails.close_time.slice(0, 5) : '21:00'}
                        onBlur={async (e) => {
                          const val = e.target.value;
                          if (!val) return;
                          try {
                            await api.updatePvz(pvzDetails.id, { close_time: val });
                            toast.success(`Время закрытия ПВЗ обновлено: ${val}`);
                            setPvzDetails(prev => ({ ...prev, close_time: val }));
                          } catch (err) {
                            toast.error(err.message);
                          }
                        }}
                        style={{ height: '38px', fontSize: '14px', padding: '0 8px' }}
                      />
                    </div>
                  </div>
                </div>

                {/* КОЛОНКА 2: Часы автоматических смен */}
                <div className="auto-shift-col">
                  <strong className="auto-shift-col-title">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    Расписание автосмен
                  </strong>
                  <span className="auto-shift-col-desc">Во сколько открывать/закрывать смену по умолчанию</span>
                  
                  <div className="auto-shift-inputs-row">
                    <div className="auto-shift-input-group">
                      <label className="auto-shift-input-label">Автооткрытие</label>
                      <input 
                        className="input" 
                        type="time" 
                        defaultValue={pvzDetails.auto_open_time ? pvzDetails.auto_open_time.slice(0, 5) : '09:00'}
                        onBlur={async (e) => {
                          const val = e.target.value;
                          if (!val) return;
                          try {
                            await api.updatePvz(pvzDetails.id, { auto_open_time: val });
                            toast.success(`Время автооткрытия установлено: ${val}`);
                            setPvzDetails(prev => ({ ...prev, auto_open_time: val }));
                          } catch (err) {
                            toast.error(err.message);
                          }
                        }}
                        style={{ height: '38px', fontSize: '14px', padding: '0 8px' }}
                      />
                    </div>
                    <div className="auto-shift-input-group">
                      <label className="auto-shift-input-label">Автозакрытие</label>
                      <input 
                        className="input" 
                        type="time" 
                        defaultValue={pvzDetails.auto_close_time ? pvzDetails.auto_close_time.slice(0, 5) : '21:00'}
                        onBlur={async (e) => {
                          const val = e.target.value;
                          if (!val) return;
                          try {
                            await api.updatePvz(pvzDetails.id, { auto_close_time: val });
                            toast.success(`Время автозакрытия установлено: ${val}`);
                            setPvzDetails(prev => ({ ...prev, auto_close_time: val }));
                          } catch (err) {
                            toast.error(err.message);
                          }
                        }}
                        style={{ height: '38px', fontSize: '14px', padding: '0 8px' }}
                      />
                    </div>
                  </div>
                </div>

                {/* КОЛОНКА 3: Активация авторежимов */}
                <div className="auto-shift-col-transparent">
                  
                  {/* Переключатель 1 */}
                  <label className="auto-shift-toggle-label">
                    <div className="auto-shift-toggle-text-container">
                      <span className="auto-shift-toggle-title">Автооткрытие смены</span>
                      <span className="auto-shift-toggle-desc">Если сотрудник забыл нажать кнопку</span>
                    </div>
                    <div className="auto-shift-toggle-switch">
                      <input 
                        type="checkbox" 
                        className="auto-shift-toggle-checkbox"
                        checked={pvzDetails.auto_open_shift || false} 
                        onChange={async (e) => {
                          const val = e.target.checked
                          try {
                            await api.updatePvz(pvzDetails.id, { auto_open_shift: val })
                            toast.success(val ? 'Автооткрытие смен включено' : 'Автооткрытие смен выключено')
                            setPvzDetails(prev => ({ ...prev, auto_open_shift: val }))
                          } catch (err) {
                            toast.error(err.message)
                          }
                        }}
                      />
                      <span className="auto-shift-toggle-slider"></span>
                    </div>
                  </label>

                  {/* Переключатель 2 */}
                  <label className="auto-shift-toggle-label">
                    <div className="auto-shift-toggle-text-container">
                      <span className="auto-shift-toggle-title">Автозакрытие смены</span>
                      <span className="auto-shift-toggle-desc">Если сотрудник забыл закрыть смену</span>
                    </div>
                    <div className="auto-shift-toggle-switch">
                      <input 
                        type="checkbox" 
                        className="auto-shift-toggle-checkbox"
                        checked={pvzDetails.auto_close_shift || false} 
                        onChange={async (e) => {
                          const val = e.target.checked
                          try {
                            await api.updatePvz(pvzDetails.id, { auto_close_shift: val })
                            toast.success(val ? 'Автозакрытие смен включено' : 'Автозакрытие смен выключено')
                            setPvzDetails(prev => ({ ...prev, auto_close_shift: val }))
                          } catch (err) {
                            toast.error(err.message)
                          }
                        }}
                      />
                      <span className="auto-shift-toggle-slider"></span>
                    </div>
                  </label>

                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      {canManageSchedule && (
        <div style={{ marginBottom: '20px', maxWidth: '340px' }}>
          <EmployeeSelector
            employees={employees.filter(e => !e.is_legacy || scheduleData.some(s => s.employee_id === e.id))}
            value={filterEmployee}
            onChange={setFilterEmployee}
            getEmployeeColor={getEmployeeColor}
          />
        </div>
      )}

      {/* Pending Requests */}
      {canManageSchedule && pendingRequests.length > 0 && (
        <div className="card" style={{ marginBottom: '20px', padding: '16px', borderLeft: '4px solid var(--warning)' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span> Запросы на добавление смен за прошедшие дни
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pendingRequests.map(req => (
              <div key={`${req.employee_id}_${req.work_date}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-secondary)', padding: '10px 12px', borderRadius: '8px' }}>
                <div>
                  <strong style={{ display: 'block', fontSize: '14px' }}>{req.employee_name}</strong>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Дата: {req.work_date} • Часы: {req.hours} {req.shift_role === 'helper' && '(Помощник)'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-sm" style={{ background: 'var(--success)', color: 'white', border: 'none' }} onClick={() => handleResolveRequest(req.employee_id, req.work_date, true)}>Одобрить</button>
                  <button className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} onClick={() => handleResolveRequest(req.employee_id, req.work_date, false)}>Отклонить</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit Logs */}
      {canManageSchedule && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div 
            style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setShowAuditLogs(!showAuditLogs)}
          >
            <h3 style={{ margin: 0, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
              <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l4 2"/></svg></span> История изменений графика
            </h3>
            <span style={{ color: 'var(--text-tertiary)', transform: showAuditLogs ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-flex' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></span>
          </div>
          
          {showAuditLogs && (
            <div style={{ padding: '0 16px 16px 16px', maxHeight: '300px', overflowY: 'auto' }}>
              {auditLogs.length === 0 ? (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                  История изменений пока пуста. Внесите изменения в график, чтобы они появились здесь.
                </div>
              ) : (
                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--surface-border)', textAlign: 'left' }}>
                      <th style={{ padding: '8px', fontWeight: 500 }}>Дата смены</th>
                      <th style={{ padding: '8px', fontWeight: 500 }}>Сотрудник</th>
                      <th style={{ padding: '8px', fontWeight: 500 }}>Действие</th>
                      <th style={{ padding: '8px', fontWeight: 500 }}>Кто изменил</th>
                      <th style={{ padding: '8px', fontWeight: 500 }}>Время изменения</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map(log => {
                      let actionText = log.action
                      let actionColor = 'var(--text-primary)'
                      if (log.action === 'created') { actionText = 'Добавлено'; actionColor = 'var(--success)' }
                      else if (log.action === 'updated') { actionText = 'Изменено'; actionColor = 'var(--info)' }
                      else if (log.action === 'deleted') { actionText = 'Удалено'; actionColor = 'var(--danger)' }
                      else if (log.action === 'approved') { actionText = 'Одобрено'; actionColor = 'var(--success)' }
                      else if (log.action === 'rejected') { actionText = 'Отклонено'; actionColor = 'var(--danger)' }
                      
                      return (
                        <tr key={log.id} style={{ borderBottom: '1px solid var(--surface-border)' }}>
                          <td style={{ padding: '8px' }}>{new Date(log.work_date).toLocaleDateString('ru-RU')}</td>
                          <td style={{ padding: '8px' }}>{log.employee_name || 'Неизвестный'}</td>
                          <td style={{ padding: '8px', color: actionColor, fontWeight: 500 }}>{actionText}</td>
                          <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>{log.changer_name || 'Система'}</td>
                          <td style={{ padding: '8px', color: 'var(--text-tertiary)' }}>{new Date(log.created_at).toLocaleString('ru-RU', {day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'})}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}



      {/* Grid и AI Хелпер */}
      <div className="schedule-flex-container" style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap', width: '100%', marginTop: '20px' }}>
        <div className="schedule-grid-wrapper" data-tour="schedule-grid" style={{ flex: '3', minWidth: '350px' }}>
          {loading ? (
            <div className="schedule-skeleton-grid">
              <div className="schedule-skeleton-header">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="skeleton-pulse" style={{ width: '38px', height: '40px', borderRadius: '6px' }} />
              ))}
            </div>
            {Array.from({ length: 3 }).map((_, r) => (
              <div key={r} className="schedule-skeleton-row">
                <div className="skeleton-pulse" style={{ width: '120px', height: '16px', borderRadius: '4px' }} />
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="skeleton-pulse" style={{ width: '38px', height: '38px', borderRadius: '6px' }} />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="schedule-grid" style={{ '--days': daysCount }}>
            {/* Header Row */}
            <div className="schedule-grid-header">
              <div className="schedule-grid-cell schedule-name-cell">Сотрудник</div>
              {Array.from({ length: daysCount }, (_, i) => {
                const day = i + 1
                const wd = getWeekday(year, month, day)
                const isWeekend = wd >= 5
                
                // ИИ-прогноз для подсветки дней
                const dayData = aiForecastingEnabled ? getDayWorkload(day) : null
                const workload = dayData?.workload
                
                let highlightClass = ''
                if (workload) {
                  highlightClass = `has-workload workload-${workload.toLowerCase()}`
                }

                return (
                  <div
                    key={day}
                    className={`schedule-grid-cell schedule-day-header ${isWeekend ? 'weekend' : ''} ${highlightClass}`}
                  >
                    <span className="schedule-day-number">{day}</span>
                    <span className="schedule-day-name">{WEEKDAY_SHORT[wd]}</span>
                    
                    {dayData && (
                      <div className={`schedule-day-neon-line ${workload.toLowerCase()}`} />
                    )}
                    
                    {dayData && (
                      <div className="schedule-day-workload-tooltip animate-fade-in">
                        <div className="tooltip-title">
                          {dayData.isHistorical ? 'Исторический прогноз:' : 'Прогноз нагрузки ИИ:'} {dayData.dayOfWeek}
                        </div>
                        <div className="tooltip-load-row">
                          Нагрузка: <strong className={`load-${workload.toLowerCase()}`}>{workload === 'High' ? 'Пиковая' : workload === 'Low' ? 'Низкая' : 'Умеренная'}</strong>
                        </div>
                        <div className="tooltip-metrics">
                          Ожидается приёмка: <strong>{dayData.expectedAcceptance} шт.</strong><br/>
                          Ожидается выдача: <strong>{dayData.expectedIssued} шт.</strong>
                        </div>
                        <div className="tooltip-recommendation">
                          {dayData.recommendation}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
              <div className="schedule-grid-cell schedule-total-header">Дней</div>
              <div className="schedule-grid-cell schedule-total-header">Часов</div>
            </div>

            {/* Employee Rows grouped by PVZ */}
            {(() => {
              const grouped = getGroupedEmployees()
              return grouped.map((group) => (
                <div key={group.pvzId || 'flat'} style={{ display: 'contents' }}>
                  {group.pvzName && (() => {
                    const pvzColor = group.pvzId && group.pvzId !== 'other' ? getPvzColor(group.pvzId) : 'var(--text-tertiary)';
                    return (
                      <div 
                        className="schedule-pvz-group-row"
                        style={{ borderLeft: `3px solid ${pvzColor}` }}
                      >
                        <span className="schedule-pvz-group-icon" style={{ color: pvzColor }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                            <polyline points="9 22 9 12 15 12 15 22"/>
                          </svg>
                        </span>
                        <span className="schedule-pvz-group-title">{group.pvzName}</span>
                        <span className="schedule-pvz-group-count">({group.emps.length})</span>
                      </div>
                    );
                  })()}
                  {group.emps.map((emp) => {
                    const totals = getTotals(emp.id)
                    const empColor = getEmployeeColor(emp.id)
                    return (
                      <div key={emp.id} className="schedule-grid-row">
                        <div className="schedule-grid-cell schedule-name-cell">
                          <div className="schedule-emp-name" title={emp.name}>
                            {emp.name}
                          </div>
                        </div>
                        {Array.from({ length: daysCount }, (_, i) => {
                          const day = i + 1
                          const hours = getHours(emp.id, day)
                          const entry = getEntry(emp.id, day)
                          const cellType = getCellType(entry)
                          const cellKey = `${emp.id}_${day}`
                          const isEditing = editingCell?.empId === emp.id && editingCell?.day === day
                          const isSaving = savingCell === cellKey
                          const canEdit = canManageSchedule || emp.id === profile.id

                          return (
                            <div
                              key={day}
                              className={`schedule-grid-cell schedule-hour-cell ${canEdit ? 'editable' : ''} ${cellType ? `cell-${cellType}` : ''}`}
                              title={entry ? `Пункт: ${getPvzFullName(entry.pvz_id)}` : ''}
                              style={{
                                background: cellType === 'active' 
                                  ? `color-mix(in srgb, #10B981 20%, transparent)` 
                                  : cellType === 'actual' 
                                    ? `color-mix(in srgb, ${empColor} 15%, transparent)` 
                                    : cellType === 'planned' 
                                      ? `color-mix(in srgb, ${empColor} 7%, transparent)` 
                                      : hours > 0 
                                        ? `color-mix(in srgb, ${empColor} 12%, transparent)` 
                                        : '',
                              }}
                              onClick={() => {
                                if (isSaving) return
                                if (!canEdit) return
                                startEdit(emp.id, day)
                              }}
                            >
                              {isSaving ? (
                                <span className="schedule-saving"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>
                              ) : cellType === 'active' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <span className="schedule-hours-value" style={{ color: '#10B981', fontSize: '11px' }}>{(() => { const elapsed = (Date.now() - new Date(entry.started_at).getTime()) / (1000*60*60); return elapsed > 0 ? `${Math.floor(elapsed)}ч` : ''; })()}</span>
                                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10B981', animation: 'pulse 2s infinite' }}></span>
                                  {renderPvzBadge(entry)}
                                </div>
                              ) : cellType === 'planned' && !entry?.started_at ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <span style={{ color: empColor, fontSize: '14px', opacity: 0.6 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
                                  {renderPvzBadge(entry)}
                                </div>
                              ) : hours > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <span className="schedule-hours-value" style={{ color: empColor }}>{hours}</span>
                                  {getShiftRole(emp.id, day) === 'helper' && (
                                    <span style={{ fontSize: '8px', color: 'var(--wb-primary)', marginTop: '1px', fontWeight: 700, background: 'rgba(203, 17, 171, 0.1)', padding: '0 3px', borderRadius: '3px', lineHeight: '12px' }}>П</span>
                                  )}
                                  {entry?.approval_status === 'pending' && (
                                    <span style={{ fontSize: '8px', color: 'var(--warning)', marginTop: '1px', fontWeight: 700, padding: '0 3px', borderRadius: '3px', lineHeight: '12px' }}><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>
                                  )}
                                  {entry?.breaks && entry.breaks.length > 0 && (
                                    <span style={{ fontSize: '9px', color: 'var(--warning)', marginTop: '1px' }} title={`Перерывы: ${calculateBreaksMinutes(entry.breaks)} мин`}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg></span>
                                  )}
                                  {renderPvzBadge(entry)}
                                </div>
                              ) : null}
                            </div>
                          )
                        })}
                        <div className="schedule-grid-cell schedule-total-cell">
                          <strong>{totals.days}</strong>
                        </div>
                        <div className="schedule-grid-cell schedule-total-cell">
                          <strong>{totals.hours}</strong>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))
            })()}
          </div>
        )}
      </div>

      {/* Горизонтальный ИИ-Помощник смен прямо под таблицей */}
      {canManageSchedule && aiForecastingEnabled && forecast && (
        <div className="ai-recommendations-strip animate-fade-in">
          <div className="ai-strip-glow"></div>
          <div className="ai-strip-header">
            <div className="ai-strip-title-wrapper">
              <span className="ai-stars-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></span>
              <div className="ai-strip-titles">
                <h3 className="ai-strip-title">ИИ-Помощник смен</h3>
                <p className="ai-strip-subtitle">Умный анализ и рекомендации по распределению сотрудников на 7 дней</p>
              </div>
            </div>
            <button 
              className={`btn btn-secondary btn-sm ai-strip-refresh-btn ${loadingForecast ? 'loading' : ''}`} 
              onClick={() => loadForecast(true)} 
              disabled={loadingForecast}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg className={loadingForecast ? 'spin-animation' : ''} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: loadingForecast ? 'transform 1s linear infinite' : 'none' }}>
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
              </svg>
              {loadingForecast ? 'Анализ...' : 'Обновить прогноз'}
            </button>
          </div>
          
          <div className="ai-strip-content">
            {loadingForecast ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 10px', gap: '12px', width: '100%' }}>
                <div className="ai-loading-spinner">
                  <div className="double-bounce1"></div>
                  <div className="double-bounce2"></div>
                </div>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center', fontWeight: 500 }}>
                  ИИ оценивает укомплектованность смен...
                </span>
              </div>
            ) : forecastError ? (
              <div className="error-state" style={{ padding: '30px', textAlign: 'center', width: '100%' }}>
                <p style={{ color: 'var(--danger)', margin: '0 0 12px 0', fontSize: '13px' }}>{forecastError}</p>
                <button className="btn btn-primary btn-sm" onClick={() => loadForecast(true)}>Повторить</button>
              </div>
            ) : (
              <>
                {forecast.summary && (
                  <div className="ai-strip-summary-card">
                    <div className="ai-summary-bulb"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/></svg></div>
                    <div className="ai-summary-text">{forecast.summary}</div>
                  </div>
                )}
                
                <div className="ai-strip-cards-container">
                  {forecast.forecast?.map((day, idx) => {
                    const isHigh = day.workload === 'High'
                    const isLow = day.workload === 'Low'
                    
                    let statusClass = 'medium'
                    let statusText = 'Норма'
                    if (isHigh) {
                      statusClass = 'high'
                      statusText = 'Пиковая нагрузка'
                    } else if (isLow) {
                      statusClass = 'low'
                      statusText = 'Спад'
                    }
                    
                    const hasWarning = day.recommendation && day.recommendation !== 'Смена укомплектована оптимально.' && !day.recommendation.includes('оптимально')
                    
                    return (
                      <div key={idx} className={`ai-recommendation-card status-${statusClass} ${hasWarning ? 'has-warning' : ''}`}>
                        <div className="ai-card-header">
                          <div className="ai-card-date-group">
                            <span className="ai-card-day-name">{day.dayOfWeek}</span>
                            <span className="ai-card-date">{day.date.split('-').reverse().slice(0, 2).join('.')}</span>
                          </div>
                          <span className={`ai-card-badge badge-${statusClass}`}>{statusText}</span>
                        </div>
                        
                        <div className="ai-card-body">
                          <div className="ai-card-metric-row">
                            <span>Приёмка: <strong>{day.expectedAcceptance} шт.</strong></span>
                            <span>Выдача: <strong>{day.expectedIssued} шт.</strong></span>
                          </div>
                          
                          <div className="ai-card-staff">
                            <span className="ai-card-staff-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></span>
                            <span className="ai-card-staff-list">
                              {day.scheduledStaff && day.scheduledStaff.length > 0 
                                ? day.scheduledStaff.join(', ') 
                                : <span className="no-staff">Нет сотрудников!</span>
                              }
                            </span>
                          </div>
                        </div>
                        
                        {day.recommendation && (
                          <div className="ai-card-footer">
                            {day.recommendation}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>      {/* Edit Modal */}
      <Modal
        isOpen={!!editingCell}
        onClose={() => setEditingCell(null)}
        title="Управление сменой"
        isBottomSheet={true}
        onConfirm={() => editingCell && saveCell(editingCell.empId, editingCell.day)}
      >
        {editingCell && (() => {
          const { empId, day } = editingCell
          const emp = employees.find(e => e.id === empId) || {}
          const entry = getEntry(empId, day)
          const cellType = getCellType(entry)
          
          return (
            <div className="schedule-modal-content">
              <div className="schedule-modal-info">
                <strong>{emp.name}</strong> • {day} {MONTH_NAMES[month - 1]} {year}
              </div>
              
              {/* Выбор ПВЗ для планируемой смены (показываем только если выбрано несколько ПВЗ) */}
              {selectedPvzIds && selectedPvzIds.length > 1 && (
                <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--surface-border)', marginBottom: '8px' }}>
                  <CustomSelect
                    label="Пункт выдачи (ПВЗ)"
                    value={editPvzId}
                    onChange={setEditPvzId}
                    options={(userPvzs || []).filter(p => selectedPvzIds.includes(p.id)).map(p => ({
                      value: p.id,
                      label: p.name
                    }))}
                  />
                </div>
              )}
              
              {/* Переключатель режимов */}
              <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', borderRadius: '10px', padding: '4px', marginBottom: '16px' }}>
                <button
                  style={{
                    flex: 1, padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontWeight: 600, fontSize: '13px', transition: 'all 0.2s',
                    background: editMode === 'planned' ? 'var(--wb-primary)' : 'transparent',
                    color: editMode === 'planned' ? 'white' : 'var(--text-secondary)',
                  }}
                  onClick={() => setEditMode('planned')}
                >
                  Запланировать
                </button>
                <button
                  style={{
                    flex: 1, padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontWeight: 600, fontSize: '13px', transition: 'all 0.2s',
                    background: editMode === 'hours' ? 'var(--wb-primary)' : 'transparent',
                    color: editMode === 'hours' ? 'white' : 'var(--text-secondary)',
                  }}
                  onClick={() => setEditMode('hours')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Указать часы
                </button>
              </div>

              {editMode === 'planned' ? (
                /* === Режим «Запланировать» === */
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '48px', marginBottom: '8px' }}><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--wb-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>
                    Плановая смена. Часы будут засчитаны<br/>когда сотрудник нажмёт «Начать смену».
                  </p>
                </div>
              ) : (
                /* === Режим «Указать часы» === */
                <>
                  {/* Время начала и конца */}
                  <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--surface-border)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '10px' }}>Время смены</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div className="input-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '11px' }}>Начало</label>
                        <input
                          className="input"
                          type="time"
                          value={editStartTime}
                          onChange={(e) => {
                            setEditStartTime(e.target.value)
                            if (e.target.value && editEndTime) {
                              const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                              const diffMs = new Date(`${dateStr}T${editEndTime}:00`) - new Date(`${dateStr}T${e.target.value}:00`)
                              if (diffMs > 0) setEditValue((Math.round((diffMs / (1000*60*60)) * 2) / 2).toString())
                            }
                          }}
                        />
                      </div>
                      <div className="input-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '11px' }}>Конец</label>
                        <input
                          className="input"
                          type="time"
                          value={editEndTime}
                          onChange={(e) => {
                            setEditEndTime(e.target.value)
                            if (editStartTime && e.target.value) {
                              const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                              const diffMs = new Date(`${dateStr}T${e.target.value}:00`) - new Date(`${dateStr}T${editStartTime}:00`)
                              if (diffMs > 0) setEditValue((Math.round((diffMs / (1000*60*60)) * 2) / 2).toString())
                            }
                          }}
                        />
                      </div>
                    </div>
                    {editStartTime && editEndTime && (() => {
                      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                      const diffMs = new Date(`${dateStr}T${editEndTime}:00`) - new Date(`${dateStr}T${editStartTime}:00`)
                      const diffH = diffMs > 0 ? (Math.round((diffMs / (1000*60*60)) * 2) / 2) : 0
                      return <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '8px' }}>Итого: <strong style={{ color: 'var(--text-primary)', fontSize: '15px' }}>{diffH} ч</strong></div>
                    })()}
                  </div>

                  {/* Или ввести часы вручную */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '12px 0' }}>
                    <div style={{ flex: 1, height: '1px', background: 'var(--surface-border)' }}></div>
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>или часы вручную</span>
                    <div style={{ flex: 1, height: '1px', background: 'var(--surface-border)' }}></div>
                  </div>

                  <div className="schedule-quick-hours">
                    {[4, 6, 8, 12].map(h => (
                      <button 
                        key={h}
                        className={`btn btn-sm ${editValue === h.toString() ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => { setEditValue(h.toString()); setEditStartTime(''); setEditEndTime('') }}
                      >
                        {h} ч
                      </button>
                    ))}
                  </div>

                  <input
                    className="input schedule-modal-input"
                    type="number"
                    min="0"
                    max="24"
                    step="0.5"
                    value={editValue === 0 || editValue === '0' ? '' : editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, empId, day)}
                    placeholder="Часы (0-24)"
                  />
                </>
              )}
              
              {/* Роль/привязка помощника */}
              {salaryConfig?.enableHelpers !== false && emp.role === 'employee' && editMode === 'hours' && (
                <div className="input-group" style={{ marginTop: '12px', marginBottom: '8px' }}>
                  <CustomSelect
                    label="Роль на эту смену"
                    value={editShiftRole}
                    onChange={val => { setEditShiftRole(val); if (val !== 'helper') setEditLinkedTo('') }}
                    options={[
                      { value: 'employee', label: 'Основной сотрудник' },
                      { value: 'helper', label: 'Помощник' },
                    ]}
                  />
                </div>
              )}
              {salaryConfig?.enableHelpers !== false && (emp.role === 'helper' || editShiftRole === 'helper') && editMode === 'hours' && (
                <div className="input-group" style={{ marginTop: '8px', marginBottom: '12px' }}>
                  <CustomSelect
                    label="Привязка помощника"
                    value={editLinkedTo}
                    onChange={setEditLinkedTo}
                    options={[
                      { value: '', label: 'Без привязки' },
                      ...employees.filter(e => e.id !== empId && e.role !== 'helper' && !e.is_legacy).map(e => ({
                        value: e.id,
                        label: e.name,
                      })),
                    ]}
                  />
                </div>
              )}
              
              {/* Детализация перерывов */}
              {entry?.breaks && entry.breaks.length > 0 && (
                <div style={{ marginTop: '16px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--surface-border)', textAlign: 'left' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg></span> Детализация перерывов ({entry.breaks.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {entry.breaks.map((b, idx) => {
                      const startStr = b.start ? new Date(b.start).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : ''
                      const endStr = b.end ? new Date(b.end).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : 'идёт...'
                      const durationMs = b.start && b.end ? new Date(b.end) - new Date(b.start) : (b.start ? Date.now() - new Date(b.start).getTime() : 0)
                      const durationMin = Math.round(durationMs / 60000)
                      return (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--text-primary)' }}>
                          <span>Перерыв #{idx + 1} ({startStr} - {endStr})</span>
                          <span style={{ fontWeight: 600, color: durationMin > 15 ? 'var(--danger)' : 'var(--text-secondary)' }}>
                            {durationMin} мин {durationMin > 15 && '(превышение!)'}
                          </span>
                        </div>
                      )
                    })}
                    <div style={{ borderTop: '1px solid var(--surface-border)', paddingTop: '6px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 700 }}>
                      <span>Всего отдыха:</span>
                      <span style={{ color: 'var(--wb-primary)' }}>{calculateBreaksMinutes(entry.breaks)} мин</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="schedule-modal-actions">
                <button className="btn btn-primary" onClick={() => saveCell(empId, day)}>
                  {editMode === 'planned' ? 'Запланировать' : 'Сохранить часы'}
                </button>
                {cellType && (
                  <button 
                    className="btn btn-ghost" 
                    style={{ color: 'var(--danger)' }}
                    onClick={() => { if (window.confirm('Удалить эту запись?')) deleteCell(empId, day) }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg> Удалить
                  </button>
                )}
                <button className="btn btn-ghost" onClick={() => setEditingCell(null)}>
                  Отмена
                </button>
              </div>
            </div>
          )
        })()}
      </Modal>
    </div>
  )
}
